import React, { useEffect, useMemo, useState } from 'react';
import planillaSeguroService from '../services/planillaSeguroService';
import { CODIGOS_SEGURO_PLANILLA } from '../constants/segurosPlanilla';

const formatearMonto = (valor) =>
  Number(valor || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SegurosPlanillaModal = ({
  isOpen,
  onClose,
  planillaId,
  planillaNombre,
  empleados = [],
  onActualizarResumen,
  onRecalcularPlanilla,
}) => {
  const [codigoSeleccionado, setCodigoSeleccionado] = useState(CODIGOS_SEGURO_PLANILLA[0].codigo);
  const [montosPorEmpleado, setMontosPorEmpleado] = useState({});
  const [registrosGuardados, setRegistrosGuardados] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [error, setError] = useState(null);

  const empleadosOrdenados = useMemo(
    () =>
      [...empleados].sort((a, b) =>
        String(a.nombre || a.nombre_completo || '').localeCompare(
          String(b.nombre || b.nombre_completo || ''),
          'es'
        )
      ),
    [empleados]
  );

  const cargarSeguros = async () => {
    if (!planillaId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await planillaSeguroService.obtenerSeguros(planillaId);
      if (response.success) {
        setRegistrosGuardados(response.data.registros || []);
        setResumen(response.data.resumen || []);
        onActualizarResumen?.(response.data);
      } else {
        setError(response.message || 'No se pudieron cargar los seguros');
      }
    } catch (err) {
      console.error('Error cargando seguros:', err);
      setError('Error de conexión al cargar seguros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && planillaId) {
      cargarSeguros();
    }
  }, [isOpen, planillaId]);

  useEffect(() => {
    if (!isOpen) return;

    const montosIniciales = {};
    empleadosOrdenados.forEach((empleado) => {
      const registro = registrosGuardados.find(
        (row) =>
          String(row.codigo_plame) === codigoSeleccionado &&
          Number(row.empleado_id) === Number(empleado.id)
      );
      montosIniciales[empleado.id] = registro ? String(registro.monto) : '';
    });
    setMontosPorEmpleado(montosIniciales);
  }, [isOpen, codigoSeleccionado, registrosGuardados, empleadosOrdenados]);

  const totalCodigoActual = useMemo(() => {
    return Object.values(montosPorEmpleado).reduce((sum, valor) => {
      const monto = parseFloat(valor);
      return sum + (Number.isNaN(monto) ? 0 : monto);
    }, 0);
  }, [montosPorEmpleado]);

  const empleadosConMontoActual = useMemo(() => {
    return Object.values(montosPorEmpleado).filter((valor) => parseFloat(valor) > 0).length;
  }, [montosPorEmpleado]);

  const handleMontoChange = (empleadoId, valor) => {
    setMontosPorEmpleado((prev) => ({
      ...prev,
      [empleadoId]: valor,
    }));
  };

  const ejecutarRecalculoPlanilla = async () => {
    if (!onRecalcularPlanilla || typeof onRecalcularPlanilla !== 'function') {
      return;
    }

    setRecalculando(true);
    try {
      await onRecalcularPlanilla();
    } catch (err) {
      console.error('Error al recalcular planilla tras seguros:', err);
      throw err;
    } finally {
      setRecalculando(false);
    }
  };

  const handleGuardar = async () => {
    if (!planillaId) return;

    setSaving(true);
    setError(null);

    try {
      const montos = empleadosOrdenados.map((empleado) => ({
        empleado_id: empleado.id,
        monto: parseFloat(montosPorEmpleado[empleado.id] || 0) || 0,
      }));

      const response = await planillaSeguroService.guardarSeguro(
        planillaId,
        codigoSeleccionado,
        montos
      );

      if (response.success) {
        setRegistrosGuardados(response.data.registros || []);
        setResumen(response.data.resumen || []);
        onActualizarResumen?.(response.data);
        await ejecutarRecalculoPlanilla();
      } else {
        setError(response.message || 'No se pudo guardar el seguro');
      }
    } catch (err) {
      console.error('Error guardando seguros:', err);
      setError(err?.message || 'Error de conexión al guardar seguros');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarCodigo = async () => {
    if (!planillaId) return;
    if (!window.confirm(`¿Eliminar todos los montos del seguro ${codigoSeleccionado}?`)) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await planillaSeguroService.eliminarSeguro(planillaId, codigoSeleccionado);
      if (response.success) {
        setRegistrosGuardados(response.data.registros || []);
        setResumen(response.data.resumen || []);
        onActualizarResumen?.(response.data);
        setMontosPorEmpleado({});
        await ejecutarRecalculoPlanilla();
      } else {
        setError(response.message || 'No se pudo eliminar el seguro');
      }
    } catch (err) {
      console.error('Error eliminando seguro:', err);
      setError(err?.message || 'Error de conexión al eliminar seguros');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const procesando = saving || recalculando;
  const seguroActual = CODIGOS_SEGURO_PLANILLA.find((item) => item.codigo === codigoSeleccionado);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold">Seguros de Planilla</h2>
              <p className="text-indigo-100 text-sm mt-1">
                {planillaNombre || 'Planilla'} · Aportes PLAME 0803, 0805, 0806 y 0810
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:text-indigo-100 text-2xl p-2 rounded-lg hover:bg-white hover:bg-opacity-20"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">1. Selecciona el seguro</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CODIGOS_SEGURO_PLANILLA.map((seguro) => {
                const resumenCodigo = resumen.find((item) => item.codigo_plame === seguro.codigo);
                const activo = codigoSeleccionado === seguro.codigo;

                return (
                  <button
                    key={seguro.codigo}
                    type="button"
                    onClick={() => setCodigoSeleccionado(seguro.codigo)}
                    className={`text-left border rounded-lg p-4 transition-colors ${
                      activo
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-900">{seguro.codigo}</span>
                      {resumenCodigo && (
                        <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full">
                          S/ {formatearMonto(resumenCodigo.total_monto)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{seguro.descripcionCorta}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">2. Montos por trabajador</h3>
                <p className="text-xs text-gray-500 mt-1">{seguroActual?.descripcion}</p>
              </div>
              <div className="text-sm text-gray-600">
                Total {codigoSeleccionado}:{' '}
                <span className="font-semibold text-indigo-700">S/ {formatearMonto(totalCodigoActual)}</span>
                {' · '}
                {empleadosConMontoActual} trabajador(es)
              </div>
            </div>

            {loading ? (
              <div className="py-10 text-center text-gray-500">Cargando seguros...</div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[45vh]">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Trabajador
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          DNI
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Cargo
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase w-40">
                          Monto (S/)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {empleadosOrdenados.map((empleado) => (
                        <tr key={empleado.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {empleado.nombre || empleado.nombre_completo}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {empleado.documento || empleado.numero_documento || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{empleado.cargo || '-'}</td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={montosPorEmpleado[empleado.id] ?? ''}
                              onChange={(e) => handleMontoChange(empleado.id, e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-right focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-600">
            Seguros activos: {resumen.length} · Total general: S/ {formatearMonto(resumen.reduce((sum, item) => sum + Number(item.total_monto || 0), 0))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleEliminarCodigo}
              disabled={procesando}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Eliminar {codigoSeleccionado}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={procesando}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={handleGuardar}
              disabled={procesando || loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {recalculando
                ? 'Recalculando planilla...'
                : saving
                  ? 'Guardando...'
                  : onRecalcularPlanilla
                    ? `Guardar y recalcular ${codigoSeleccionado}`
                    : `Guardar ${codigoSeleccionado}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegurosPlanillaModal;
